import { useQuery } from '@tanstack/react-query';
import type { TaxBreakdownRow } from '@sellokart/shared';
import { PageHeader } from '@/components/PageHeader';
import { AsyncSection } from '@/components/AsyncSection';
import { ReportFilterBar } from '@/components/ReportFilterBar';
import {
  ReportColumnsButton,
  ReportFiltersButton,
  ReportPeriodButton,
  ReportSearchField,
  ReportToolbar,
} from '@/components/ReportToolbar';
import { applyReportLayout, DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { fetchTax, reportKeys } from '@/features/reports/api';
import { useReportFilters } from '@/features/reports/useReportFilters';
import { useReportToolbar } from '@/features/reports/useReportToolbar';
import { useReportLayout } from '@/features/reports/useReportLayout';
import { applyClientFilters } from '@/features/reports/applyClientFilters';
import { usePortal } from '@/features/portal/PortalContext';

const money = (value: number) => value.toFixed(2);

const FILTER_FIELDS = ['Component'] as const;
const TOGGLEABLE = ['amount'] as const;
const COLUMN_LABELS: Record<string, string> = { amount: 'Amount' };

const TAX_COLUMNS: Column<TaxBreakdownRow>[] = [
  { key: 'label', header: 'Component', pinned: true, cell: (row) => `${row.label} (${row.rate}%)` },
  { key: 'amount', header: 'Amount', align: 'right', cell: (row) => money(row.amount) },
];

/** Server already sorts descending by amount — the client only renders it. */
export function TaxReportPage() {
  const { profile } = usePortal();
  const [filters, setFilters] = useReportFilters();
  const toolbar = useReportToolbar(filters, setFilters);
  const layout = useReportLayout('tax', TOGGLEABLE);
  const columns = applyReportLayout(TAX_COLUMNS, layout);

  const query = useQuery({
    queryKey: reportKeys.tax(filters),
    queryFn: ({ signal }) => fetchTax(filters, signal),
  });

  return (
    <div>
      <PageHeader title="Tax report" description="Tax collected by component for the selected window." />
      <ReportFilterBar
        filters={filters}
        onChange={setFilters}
        branches={profile?.branches ?? []}
        showDocType
        hideDateRange
      />
      <ReportToolbar>
        <ReportSearchField
          value={toolbar.search}
          onChange={toolbar.setSearch}
          placeholder="Search component"
        />
        <ReportFiltersButton
          fields={FILTER_FIELDS}
          applied={toolbar.filterRows}
          matchAny={toolbar.matchAny}
          onApply={toolbar.applyFilters}
          placeholderFor={() => 'component name'}
        />
        <ReportColumnsButton layout={layout} labels={COLUMN_LABELS} />
        <ReportPeriodButton
          from={filters.from}
          to={filters.to}
          onChange={toolbar.setRange}
          deselected={toolbar.filterActive}
        />
      </ReportToolbar>
      <AsyncSection
        state={query}
        select={(rows: TaxBreakdownRow[]) =>
          applyClientFilters(rows, {
            search: toolbar.search,
            filterRows: toolbar.filterRows,
            matchAny: toolbar.matchAny,
            fieldValue: (row, field) => (field === 'Component' ? row.label : undefined),
            searchValues: (row) => [row.label],
          })
        }
        isEmpty={(rows) => rows.length === 0}
        emptyTitle="No tax collected in this window"
        skeleton={<TableSkeleton columns={2} />}
      >
        {(rows) => (
          <DataTable
            caption="Tax breakdown"
            layout={layout}
            columns={columns}
            rows={rows}
            rowKey={(row, index) => `${row.label}-${index}`}
            totals={{ label: 'Total', cells: { amount: money(rows.reduce((acc, row) => acc + row.amount, 0)) } }}
          />
        )}
      </AsyncSection>
    </div>
  );
}
